import React from 'react';
import { useManualBooking } from './ManualBooking/useManualBooking';
import ModeToggle from './ManualBooking/ModeToggle';
import BookingSlotFields from './ManualBooking/BookingSlotFields';
import CourtPicker from './ManualBooking/CourtPicker';
import CustomerPicker from './ManualBooking/CustomerPicker';
import PaymentDiscountCard from './ManualBooking/PaymentDiscountCard';
import BlockDetailsCard from './ManualBooking/BlockDetailsCard';
import SummaryCard from './ManualBooking/SummaryCard';
import RecurrenceBuilder from './ManualBooking/RecurrenceBuilder';
import SeriesPreviewModal from './ManualBooking/SeriesPreviewModal';

export default function ManualBooking() {
  const f = useManualBooking();
  const showCustomerAndPayment = f.isManual || f.isSeries;

  return (
    <div data-testid="manual-booking-page" className="max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-4xl font-black font-heading uppercase">Manual Booking</h1>
      </div>

      <ModeToggle mode={f.mode} setMode={f.setMode} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <BookingSlotFields
            locations={f.locations}
            locationId={f.locationId} setLocationId={f.setLocationId}
            bookingDate={f.bookingDate} setBookingDate={f.setBookingDate} todayISO={f.todayISO}
            // Manual mode only: allow back-dating up to 30 days.
            // Recurring + Block Court omit `minDate` and stay locked to today.
            minDate={f.isManual ? f.thirtyDaysAgoISO() : undefined}
            duration={f.duration} setDuration={f.setDuration}
            timeSlot={f.timeSlot} setTimeSlot={f.setTimeSlot}
          />

          <CourtPicker
            sport={f.sport} setSport={f.setSport}
            quantity={f.quantity} changeQuantity={f.changeQuantity}
            timeSlot={f.timeSlot} locationId={f.locationId}
            allCourtsCount={f.allCourtsCount}
            availableCourts={f.availableCourts}
            selectedCourts={f.selectedCourts}
            loadingAvail={f.loadingAvail}
            toggleCourt={f.toggleCourt}
          />

          {f.isSeries && (
            <RecurrenceBuilder
              pattern={f.pattern} setPattern={f.setPattern}
              daysOfWeek={f.daysOfWeek} toggleDay={f.toggleDay}
              endMode={f.endMode} setEndMode={f.setEndMode}
              endDate={f.endDate} setEndDate={f.setEndDate}
              endAfterCount={f.endAfterCount} setEndAfterCount={f.setEndAfterCount}
              skipHolidays={f.skipHolidays} setSkipHolidays={f.setSkipHolidays}
              skipFridayPrayer={f.skipFridayPrayer} setSkipFridayPrayer={f.setSkipFridayPrayer}
              startDate={f.bookingDate}
            />
          )}

          {showCustomerAndPayment && (
            <CustomerPicker
              selectedCustomer={f.selectedCustomer}
              setSelectedCustomer={f.setSelectedCustomer}
              customerSearch={f.customerSearch}
              setCustomerSearch={f.setCustomerSearch}
              filteredCustomers={f.filteredCustomers}
              pickCustomer={f.pickCustomer}
              guestName={f.guestName} setGuestName={f.setGuestName}
              guestPhone={f.guestPhone} setGuestPhone={f.setGuestPhone}
              guestEmail={f.guestEmail} setGuestEmail={f.setGuestEmail}
            />
          )}

          {showCustomerAndPayment && (
            <PaymentDiscountCard
              paymentMethod={f.paymentMethod} setPaymentMethod={f.setPaymentMethod}
              paymentReference={f.paymentReference} setPaymentReference={f.setPaymentReference}
              discountType={f.discountType} setDiscountType={f.setDiscountType}
              discountValue={f.discountValue} setDiscountValue={f.setDiscountValue}
              discountReason={f.discountReason} setDiscountReason={f.setDiscountReason}
              restrictedToSeriesMethods={f.isSeries}
            />
          )}

          {/* Notify-customer toggle — surfaces ONLY when back-dating a
              Manual booking. Otherwise the standard confirmation-email
              behaviour applies. Same pattern as the series-cancel dialog. */}
          {f.isManual && f.isBackdated && (
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              data-testid="backdate-notify-block"
            >
              <label className="flex items-start gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={f.notifyCustomer}
                  onChange={(e) => f.setNotifyCustomer(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-amber-300"
                  data-testid="notify-customer-checkbox"
                />
                <span>
                  <strong>Notify customer by email</strong>
                  <br />
                  <span className="text-xs text-amber-800">
                    Uncheck when the customer already knows they played (e.g.
                    they paid cash in person). Confirmation email is
                    suppressed automatically for Pay Later back-dates.
                  </span>
                </span>
              </label>
            </div>
          )}

          {!f.isManual && !f.isSeries && (
            <BlockDetailsCard
              blockLabel={f.blockLabel} setBlockLabel={f.setBlockLabel}
              blockReason={f.blockReason} setBlockReason={f.setBlockReason}
            />
          )}
        </div>

        <div className="space-y-4">
          <SummaryCard
            isManual={f.isManual}
            isSeries={f.isSeries}
            locations={f.locations} locationId={f.locationId}
            bookingDate={f.bookingDate} timeSlot={f.timeSlot}
            duration={f.duration}
            sport={f.sport} quantity={f.quantity}
            selectedCourts={f.selectedCourts}
            unitPrice={f.unitPrice}
            grossTotal={f.grossTotal}
            discountAmount={f.discountAmount}
            discountType={f.discountType}
            discountValue={f.discountValue}
            finalTotal={f.finalTotal}
            paymentMethod={f.paymentMethod}
            blockLabel={f.blockLabel} blockReason={f.blockReason}
            submitting={f.submitting} handleSubmit={f.handleSubmit}
          />
        </div>
      </div>

      <SeriesPreviewModal
        open={f.previewOpen}
        onOpenChange={f.setPreviewOpen}
        loading={f.previewLoading}
        preview={f.previewData}
        error={f.previewError}
        onConfirm={f.confirmSeriesCommit}
        submitting={f.submitting}
      />
    </div>
  );
}
